import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { API } from '../utils/constants';

const UpgradeModal = ({ open, onOpenChange, subscriptionStatus }) => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const homeCookFeatures = [
    "Your personal recipe library",
    "Quick recipe import",
    "Shareable recipe links",
    "Custom filters & categories",
    "Public bio with social links",
  ];

  const creatorFeatures = [
    "Everything in Home Cook",
    "Analytics dashboard",
    "Custom branding",
  ];

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate(`/checkout?plan=${isYearly ? 'yearly' : 'monthly'}`);
  };

  const handleManageBilling = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${API}/customer-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(res.data.portal_url, '_blank');
    } catch (error) {
      toast.error("Could not open billing portal");
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = subscriptionStatus === 'active';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-2xl"
        style={{ 
          backgroundColor: '#F3EEE4',
          border: '1px solid #D6D0C2',
        }}
      >
        <DialogHeader className="text-center">
          <DialogTitle 
            style={{ 
              fontFamily: "'Instrument Serif', serif",
              color: '#51120A',
              fontSize: '1.95rem',
              lineHeight: '1.1',
              letterSpacing: '0.01em',
              textAlign: 'center'
            }}
          >
            {isActive ? 'Your Plan' : 'Less than the smoothie you now make at\u00A0home'}
          </DialogTitle>
          {subscriptionStatus === 'expired' && (
            <p 
              className="text-sm mt-1 text-center"
              style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                color: '#51120A'
              }}
            >
              Your free trial has ended. Upgrade to keep creating and editing recipes.
            </p>
          )}
          {(subscriptionStatus === 'trialing' || subscriptionStatus === 'none') && (
            <p 
              className="text-sm mt-1 text-center"
              style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                color: '#51120A'
              }}
            >
              You're currently on the free trial. Upgrade so you never lose your recipes again! Your payment will begin after your trial is over.
            </p>
          )}
          {isActive && (
            <p 
              className="text-sm mt-1 text-center"
              style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                color: '#51120A'
              }}
            >
              You're on the Home Cook plan. Thank you for your support!
            </p>
          )}
        </DialogHeader>

        {!isActive && (
          <>
            <div className="flex items-center justify-center gap-4 mt-4">
              <span 
                className="text-sm font-medium cursor-pointer"
                style={{ 
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: !isYearly ? '#51120A' : '#875C66',
                }}
                onClick={() => setIsYearly(false)}
              >
                Monthly
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
                style={{ backgroundColor: isYearly ? '#51120A' : '#D6D0C2' }}
              >
                <span
                  className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                  style={{ transform: isYearly ? 'translateX(26px)' : 'translateX(4px)' }}
                />
              </button>
              <span 
                className="text-sm font-medium cursor-pointer"
                style={{ 
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: isYearly ? '#51120A' : '#875C66',
                }}
                onClick={() => setIsYearly(true)}
              >
                Yearly
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div 
                className="rounded-2xl p-6 relative flex flex-col"
                style={{ 
                  backgroundColor: '#EEE7D8',
                  border: '2px solid #51120A',
                }}
              >
                <h3 
                  className="text-lg font-bold mb-1"
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A'
                  }}
                >
                  Home Cook
                </h3>
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span 
                      className="text-3xl font-bold"
                      style={{ 
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        color: '#51120A'
                      }}
                    >
                      ${isYearly ? '8.33' : '10'}
                    </span>
                    <span 
                      className="text-sm"
                      style={{ 
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        color: '#51120A'
                      }}
                    >
                      / month
                    </span>
                  </div>
                  {isYearly && (
                    <p 
                      className="text-xs mt-1"
                      style={{ 
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        color: '#51120A'
                      }}
                    >
                      billed yearly ($100)
                    </p>
                  )}
                </div>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  {homeCookFeatures.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#51120A' }} />
                      <span 
                        className="text-sm"
                        style={{ 
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                          color: '#51120A'
                        }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={handleUpgrade}
                  disabled={isLoading}
                  className="w-full py-5 text-sm font-medium transition-colors mt-auto"
                  style={{ 
                    backgroundColor: 'transparent',
                    border: '1px solid #51120A',
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  {isLoading ? 'Loading...' : 'Upgrade'}
                </Button>
              </div>

              <div 
                className="rounded-2xl p-6 relative flex flex-col"
                style={{ 
                  backgroundColor: '#EEE7D8',
                  border: '1px solid #D6D0C2',
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <h3 
                    className="text-lg font-bold"
                    style={{ 
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      color: '#51120A'
                    }}
                  >
                    Creator
                  </h3>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ 
                      backgroundColor: '#D6D0C2',
                      color: '#51120A',
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                    }}
                  >
                    Coming Soon
                  </span>
                </div>
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span 
                      className="text-3xl font-bold"
                      style={{ 
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        color: '#51120A'
                      }}
                    >
                      ${isYearly ? '16.67' : '20'}
                    </span>
                    <span 
                      className="text-sm"
                      style={{ 
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        color: '#51120A'
                      }}
                    >
                      / month
                    </span>
                  </div>
                  {isYearly && (
                    <p 
                      className="text-xs mt-1"
                      style={{ 
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        color: '#51120A'
                      }}
                    >
                      billed yearly ($200)
                    </p>
                  )}
                </div>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  {creatorFeatures.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#51120A' }} />
                      <span 
                        className="text-sm"
                        style={{ 
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                          color: '#51120A'
                        }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button 
                  disabled
                  className="w-full py-5 text-sm font-medium transition-colors cursor-not-allowed mt-auto disabled:opacity-100"
                  style={{ 
                    backgroundColor: '#D6D0C2',
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    opacity: 1,
                  }}
                >
                  Coming Soon
                </Button>
              </div>
            </div>
          </>
        )}

        {isActive && (
          <Button 
            onClick={handleManageBilling}
            disabled={isLoading}
            variant="outline"
            className="w-full py-5 font-medium transition-colors mt-4"
            style={{ 
              borderColor: '#51120A',
              color: '#51120A',
              fontFamily: "'Bricolage Grotesque', sans-serif",
            }}
          >
            {isLoading ? 'Loading...' : 'Manage Billing'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeModal;
